import React, { useContext, useEffect, useMemo, useRef, useState } from 'react';
import Chart from '../../../Chart';
import AccountContext from '../../../../contexts/AccountContext';
import areaOptions from '../../../V5Gadget/highchartOptions/areaOptions';
import Card from '../../../Common/Card/index';
import formatFloat from '../../../../api/getChartSeries/formatFloat';
import BoardContext from '../../../../contexts/BoardContext';
import isCustomerLaneBoard from '../../../../isCustomerLaneBoard';
import baseOptions from '../../../V5Gadget/highchartOptions/baseOptions';
import { getDateLabel } from '../getDateLabel';
import useValueFormatters from '../../../../hooks/useValueFormatters';
import Alert from '../../../../kingpin/Alert';

const useLineChart = ({
  data,
  config,
  cadence,
}: {
  data: MetricsResponse;
  config: TargetVisConfig;
  cadence?: 'day' | 'week' | 'month';
}) => {
  const { unitsLocale } = useContext(AccountContext);
  const { formatMetric } = useValueFormatters();
  const [options, setOptions] = useState<any>();

  useEffect(() => {
    if (data.length === 0) {
      setOptions(undefined);
      return;
    }

    const target = data[0][config.cadenceTargetField];

    const zones = [
      {
        value: target,
        color: 'var(--color-status-danger)',
      },
      { color: 'var(--color-status-success)' },
    ];

    const series = [
      {
        data: data
          .map((d) => [
            new Date(d.date).getTime(),
            formatFloat(d[config.cadenceTotalField], 0),
          ])
          .reverse(),
        name: 'performance',
        zones,
        threshold: target,
        marker: {
          enabled: false,
          symbol: 'circle',
        },
      },
    ];
    const baseAreaOptions = areaOptions({
      title: '',
      sparkline: true,
      doubleSparkHeight: true,
      formatMetric,
    });
    const options = {
      ...baseAreaOptions,
      tooltip: {
        ...baseAreaOptions.tooltip,
        formatter: function (this: any) {
          const isoDate = new Date(this.x).toISOString().substring(0, 10);
          const headerText = getDateLabel(isoDate, cadence);
          const formattedValue = formatMetric({ value: this.y });
          return `<span style="font-size: 12px; line-height: 15px; font-family: Inter;">${headerText}: </span><span style="font-size: 10px; font-family: Inter; color: #252525; line-height: 12px"><b>${formattedValue}</b></span>`;
        },
      },
      series,
    };
    setOptions(options);
  }, [
    cadence,
    config.cadenceTargetField,
    config.cadenceTotalField,
    data,
    formatMetric,
    unitsLocale,
  ]);

  return options;
};

export const useBarOptions = ({
  data,
  config,
  cadence,
}: {
  data: MetricsResponse;
  config: TargetVisConfig;
  cadence?: 'day' | 'week' | 'month';
}) => {
  const { formatMetric } = useValueFormatters();
  const [options, setOptions] = useState<any>();

  useEffect(() => {
    const options = {
      accessibility: {
        enabled: false,
      },
      chart: {
        type: 'column',
      },
      title: {
        useHTML: true,
        text: '',
      },
      plotOptions: {
        column: {
          zones: [
            {
              value: 0,
              color: 'var(--color-status-danger)',
            },
            {
              color: 'var(--color-status-success)',
            },
          ],
        },
      },
      tooltip: {
        ...baseOptions(formatMetric).tooltip,
        formatter: function () {
          // @ts-ignore
          const { x, points } = this;
          if (points.length === 0) {
            return '';
          }

          const headerText = getDateLabel(x, cadence);

          let pointsHtml = '';
          // @ts-ignore
          points.forEach((p) => {
            const formattedY = formatMetric({
              value: p.y,
              metricId: p.series.metricId,
            });
            pointsHtml += `<div><span style="color:${p.color}">● </span><span style="color: #878F9D;">${p.series.name}: </span><b>${formattedY}</b></div><br/>`;
          });

          return `<div>${
            cadence === 'week' ? 'Week' : ''
          } ${headerText}<br/>${pointsHtml}`;
        },
      },
      xAxis: {
        categories: data
          .map((d) => d[config.dateKey ? config.dateKey : 'date'])
          .reverse(),
        visible: false,
      },
      series: [
        {
          data: data
            .map((d) => formatFloat(d[config.cadenceDifferenceField], 0))
            .reverse(),
          name: config.title,
        },
      ],
      legend: {
        enabled: false,
      },
      credits: {
        enabled: false,
      },
      yAxis: {
        title: {
          enabled: false,
        },
        visible: true,
      },
    };

    setOptions(options);
  }, [
    config.cadenceDifferenceField,
    config.dateKey,
    config.title,
    data,
    formatMetric,
    cadence,
  ]);

  const hasNoTargets = useMemo(() => {
    return data
      .map((d) => d[config.cadenceDifferenceField])
      .every((diff) => diff === undefined);
  }, [config.cadenceDifferenceField, data]);

  return { barOptions: options, barOptionsHasNoTargets: hasNoTargets };
};

const PerformanceChartContainer = ({
  data,
  config,
  cadence,
}: {
  data: MetricsResponse;
  config: TargetVisConfig;
  cadence?: 'day' | 'week' | 'month';
}) => {
  const { board } = useContext(BoardContext);
  const chartRef = useRef<any>();
  const areaOptions = useLineChart({ data, config, cadence });
  const { barOptions, barOptionsHasNoTargets } = useBarOptions({
    data,
    config,
    cadence,
  });

  if (data.length === 0 || !areaOptions || !barOptions) {
    return null;
  }

  if (barOptionsHasNoTargets) {
    return <Alert type={'Default'} body={'No targets were found'} />;
  }

  return (
    <Card
      style={{
        marginBottom: 16,
        height: isCustomerLaneBoard(board) ? 'unset' : 200,
        position: 'relative',
        borderRadius: '4px',
        overflow: 'hidden',
      }}
    >
      <Chart
        chartRef={chartRef}
        options={isCustomerLaneBoard(board) ? areaOptions : barOptions}
      />
    </Card>
  );
};

export default PerformanceChartContainer;
